import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { onboardingApi, type PassosOnboarding } from '@/services/onboarding';

import styles from './PrimeirosPassos.module.css';

/**
 * O que uma conta nova vê antes de ter qualquer dado.
 *
 * A auditoria de UX mediu o primeiro segundo do produto: o cliente entra, cai
 * em `/inteligencia` e lê "0 tendências · Nenhuma tendência com estes filtros",
 * ao lado de 27 filtros de estado. Nada ali diz que falta conectar um site, que
 * falta uma chave de IA, nem qual é o próximo passo. Num produto que se vende
 * como self-service, esse é exatamente o instante em que a conta converte ou é
 * abandonada — e era onde ele ficava mudo.
 *
 * Três decisões deliberadas:
 *
 * - **Some sozinho.** Quando os quatro passos estiverem feitos o painel
 *   desaparece. Checklist que fica para sempre vira moldura e some da atenção.
 * - **Um passo em destaque por vez.** O próximo pendente fica aceso; os outros
 *   ficam legíveis mas quietos. Quatro chamadas à ação com o mesmo peso não
 *   orientam ninguém.
 * - **Cada passo diz o PORQUÊ, não só o quê.** "Conectar site" não explica
 *   nada; "é de onde vêm seus artigos e para onde o Wiredesk publica" explica.
 */

type Chave = keyof PassosOnboarding;

const PASSOS: { chave: Chave; titulo: string; porque: string; rota: string; acao: string }[] = [
  {
    chave: 'site',
    titulo: 'Conecte seu site WordPress',
    porque: 'É de onde vêm seus artigos e para onde o sistema publica. Sem site, as telas de conteúdo não têm o que mostrar.',
    rota: '/config/sites',
    acao: 'Conectar site',
  },
  {
    chave: 'ia',
    titulo: 'Informe sua chave da OpenAI',
    porque: 'É ela que escreve, cura as pautas e move o copiloto. Sem chave, a geração fica parada.',
    rota: '/config/ai-providers',
    acao: 'Adicionar chave',
  },
  {
    chave: 'marca',
    titulo: 'Defina sua marca',
    porque: 'Nome, logo e cor do seu portal. O painel passa a ser o seu — e a arte das redes sai com a sua identidade.',
    rota: '/config/marca',
    acao: 'Definir marca',
  },
  {
    chave: 'materia',
    titulo: 'Publique a primeira matéria',
    porque: 'Fecha o ciclo: da pauta até o ar, sem sair daqui. É o passo que prova que o resto ficou certo.',
    rota: '/artigos/novo',
    acao: 'Criar matéria',
  },
];

const CHAVE_OCULTAR = 'wiredesk-primeiros-passos-oculto';

export function PrimeirosPassos() {
  const [oculto, setOculto] = useState<boolean>(() => {
    // Um operador que deliberadamente não usa WordPress não pode ficar com um
    // aviso permanente na cara. localStorage falha em janela anônima e com
    // cookies bloqueados — nesses casos o painel simplesmente aparece.
    try {
      return localStorage.getItem(CHAVE_OCULTAR) === '1';
    } catch {
      return false;
    }
  });

  const { data } = useQuery({
    queryKey: ['onboarding'],
    queryFn: () => onboardingApi.resumo(),
    staleTime: 30_000,
    // Falhou? Não há painel. Este bloco é ajuda, e ajuda que dá erro na tela
    // inicial de quem acabou de assinar é pior que ajuda nenhuma.
    retry: false,
  });

  if (oculto || !data || data.concluido) return null;

  function ocultar() {
    try {
      localStorage.setItem(CHAVE_OCULTAR, '1');
    } catch {
      /* sem localStorage o painel volta no próximo carregamento — tudo bem */
    }
    setOculto(true);
  }

  const proximo = PASSOS.find((p) => !data.passos[p.chave])?.chave;

  return (
    <section className={styles.root} aria-labelledby="primeiros-passos-titulo">
      <header className={styles.head}>
        <div>
          <h2 id="primeiros-passos-titulo" className={styles.titulo}>
            Primeiros passos
          </h2>
          <p className={styles.sub}>
            Quatro coisas destravam o produto. Depois disto o painel some sozinho.
          </p>
        </div>
        <div className={styles.progresso}>
          <span className={styles.contagem}>
            {data.feitos} <span className={styles.de}>de {data.total}</span>
          </span>
          <div
            className={styles.barra}
            role="progressbar"
            aria-valuenow={data.feitos}
            aria-valuemin={0}
            aria-valuemax={data.total}
            aria-label="Progresso da configuração"
          >
            <div
              className={styles.barraCheia}
              style={{ width: `${(data.feitos / data.total) * 100}%` }}
            />
          </div>
        </div>
      </header>

      <ol className={styles.lista}>
        {PASSOS.map((p, i) => {
          const feito = data.passos[p.chave];
          const ativo = p.chave === proximo;
          return (
            <li
              key={p.chave}
              className={`${styles.passo} ${feito ? styles.feito : ''} ${ativo ? styles.ativo : ''}`}
            >
              <span className={styles.marca} aria-hidden="true">
                {feito ? '' : i + 1}
              </span>
              <div className={styles.texto}>
                <span className={styles.passoTitulo}>{p.titulo}</span>
                {!feito && <span className={styles.porque}>{p.porque}</span>}
              </div>
              {feito ? (
                <span className={styles.pronto}>pronto</span>
              ) : (
                <Link
                  to={p.rota}
                  className={ativo ? styles.botaoPrimario : styles.botaoDiscreto}
                >
                  {p.acao}
                </Link>
              )}
            </li>
          );
        })}
      </ol>

      <button type="button" className={styles.ocultar} onClick={ocultar}>
        Ocultar estes passos
      </button>
    </section>
  );
}
